import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Clock } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ShiftsTab = ({ onClearAll }) => {
  const { t } = useLanguage();
  const [shiftRequests, setShiftRequests] = useState([]);
  const [selectedShifts, setSelectedShifts] = useState([]);
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const [notesAction, setNotesAction] = useState("approve");
  const [employerNotes, setEmployerNotes] = useState("");

  const fetchShiftRequests = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/shifts/requests`, { credentials: 'include' });
      if (res.ok) setShiftRequests(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { 
    fetchShiftRequests(); 
    const interval = setInterval(fetchShiftRequests, 30000);
    return () => clearInterval(interval);
  }, [fetchShiftRequests]);

  const handleUpdateShift = async (shiftId, status, notes = null) => {
    try {
      const body = { status };
      if (notes) body.employer_notes = notes;
      const res = await fetch(`${API_URL}/api/shifts/${shiftId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify(body)
      });
      if (res.ok) { 
        toast.success(status === 'approved' ? (t("shiftApproved") || "Shift approved") : (t("shiftDenied") || "Shift denied")); 
        fetchShiftRequests(); 
      }
    } catch (e) { toast.error("Failed"); }
  };

  const handleBulkUpdate = async (status) => {
    if (selectedShifts.length === 0) {
      toast.error("Select at least one shift");
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/shifts/bulk/update`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({
          shift_ids: selectedShifts,
          status,
          employer_notes: employerNotes || null
        })
      });
      if (res.ok) { 
        toast.success(`${selectedShifts.length} shifts ${status}`); 
        setSelectedShifts([]);
        setEmployerNotes("");
        setShowNotesDialog(false);
        fetchShiftRequests(); 
      }
    } catch (e) { toast.error("Failed"); }
  };

  const toggleShiftSelection = (shiftId) => {
    setSelectedShifts(prev => 
      prev.includes(shiftId) 
        ? prev.filter(id => id !== shiftId) 
        : [...prev, shiftId]
    );
  };

  const selectAllPending = () => {
    const pendingIds = shiftRequests.filter(r => r.status === 'pending').map(r => r.shift_id);
    setSelectedShifts(pendingIds);
  };

  const openNotesDialog = (action) => {
    setNotesAction(action);
    setShowNotesDialog(true);
  };

  const pendingShifts = shiftRequests.filter(r => r.status === 'pending');

  const handleClear = async () => {
    if (onClearAll) {
      await onClearAll();
      fetchShiftRequests();
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <CardTitle>{t("shiftRequests") || "Shift Requests"} {shiftRequests.length > 0 && <span className="text-muted-foreground font-normal text-base">({shiftRequests.length} total, {pendingShifts.length} pending)</span>}</CardTitle>
              <CardDescription>{t("shiftRequestsDesc") || "Review and approve employee shift requests"}</CardDescription>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {shiftRequests.length > 0 && (
                <Button variant="ghost" size="sm" onClick={handleClear} className="text-muted-foreground hover:text-destructive">
                  Clear All
                </Button>
              )}
              {pendingShifts.length > 0 && (
                <>
                  <Button variant="outline" size="sm" onClick={selectAllPending}>
                    Select All Pending ({pendingShifts.length})
                  </Button>
                  {selectedShifts.length > 0 && (
                    <>
                      <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => openNotesDialog("approved")}>
                        {t("bulkApprove") || "Bulk Approve"} ({selectedShifts.length})
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => openNotesDialog("denied")}>
                        {t("bulkDeny") || "Bulk Deny"} ({selectedShifts.length})
                      </Button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {shiftRequests.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
              <p className="text-muted-foreground font-medium">{t("noShiftRequests") || "No shift requests"}</p>
              <p className="text-sm text-muted-foreground mt-1">{t("shiftRequestsEmptyHelper") || "Shift requests from employees will appear here"}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {shiftRequests.map((req) => (
                <div key={req.shift_id} className="p-4 rounded-xl bg-muted/50">
                  <div className="flex items-start gap-3">
                    {req.status === 'pending' && (
                      <Checkbox 
                        checked={selectedShifts.includes(req.shift_id)}
                        onCheckedChange={() => toggleShiftSelection(req.shift_id)}
                        className="mt-1"
                      />
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-medium">{req.employee_name}</span>
                        <Badge className={
                          req.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 
                          req.status === 'approved' ? 'bg-green-100 text-green-800' : 
                          req.status === 'assigned' ? 'bg-blue-100 text-blue-800' :
                          'bg-red-100 text-red-800'
                        }>{t(req.status) || req.status}</Badge>
                        {req.batch_id && <Badge variant="outline" className="text-xs">Batch</Badge>}
                      </div>
                      <p className="text-sm text-muted-foreground">{req.date} • {req.start_time} - {req.end_time}</p>
                      {req.notes && <p className="text-sm text-muted-foreground mt-1 italic">&quot;{req.notes}&quot;</p>}
                      {req.employer_notes && (
                        <div className="mt-2 p-2 bg-accent/50 rounded text-sm">
                          <span className="font-medium">{t("employerNotes") || "Employer Notes"}:</span> {req.employer_notes}
                        </div>
                      )}
                    </div>
                    {req.status === 'pending' && (
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" className="text-green-600" onClick={() => handleUpdateShift(req.shift_id, 'approved')}>
                          {t("approve") || "Approve"}
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleUpdateShift(req.shift_id, 'denied')}>
                          {t("deny") || "Deny"}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Notes Dialog for Bulk Actions */}
      <Dialog open={showNotesDialog} onOpenChange={setShowNotesDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {notesAction === 'approved' ? (t("bulkApprove") || "Bulk Approve") : (t("bulkDeny") || "Bulk Deny")} {selectedShifts.length} Shifts
            </DialogTitle>
            <DialogDescription>Optionally add notes for all selected shifts</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea 
              value={employerNotes}
              onChange={(e) => setEmployerNotes(e.target.value)}
              placeholder={t("notesPlaceholder") || "Enter notes for employees..."}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNotesDialog(false)}>{t("cancel") || "Cancel"}</Button>
            <Button 
              onClick={() => handleBulkUpdate(notesAction)}
              className={notesAction === 'approved' ? 'bg-green-600 hover:bg-green-700' : ''}
              variant={notesAction === 'denied' ? 'destructive' : 'default'}
            >
              {notesAction === 'approved' ? (t("approve") || "Approve") : (t("deny") || "Deny")} All
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ShiftsTab;
